import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Edit2, X, ExternalLink } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import GroupEditor from './GroupEditor'
import LinkAdsToEntityModal from '../shared/LinkAdsToEntityModal'
import '../SponsorManager.css'

export default function GroupDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { api } = useAuth()

  const [group, setGroup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [status, setStatus] = useState('')

  // TBTV tags
  const [tbtvTags, setTbtvTags] = useState([])
  const [allTbtvTags, setAllTbtvTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [showTagSugg, setShowTagSugg] = useState(false)

  // Link ads modal
  const [showLinkAds, setShowLinkAds] = useState(false)

  const tagInputRef = useRef(null)

  const loadGroup = useCallback(async () => {
    try {
      const data = await api(`/sponsors/groups/${id}`)
      setGroup(data.group)
      setTbtvTags((data.tbtvTags || []).map(t => t.name))
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, id])

  const loadAllTags = useCallback(async () => {
    try {
      const data = await api('/tags')
      setAllTbtvTags(data.tags || [])
    } catch {}
  }, [api])

  useEffect(() => {
    setLoading(true)
    Promise.all([loadGroup(), loadAllTags()]).finally(() => setLoading(false))
  }, [loadGroup, loadAllTags])

  async function handleEditSubmit(body) {
    const res = await api(`/sponsors/groups/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
    if (res?.ok === false) {
      throw new Error(res.error?.message || 'AdSpeed rejected the edit')
    }
    setEditing(false)
    setStatus('Group updated')
    await loadGroup()
  }

  async function persistTbtvTags(next) {
    try {
      await api(`/sponsors/groups/${id}/tbtv-tags`, {
        method: 'PUT',
        body: JSON.stringify({ tags: next }),
      })
      setStatus('Tags saved')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      await loadGroup()
    }
  }

  function addTag(name) {
    const clean = name.trim().toLowerCase()
    if (!clean || tbtvTags.includes(clean)) return
    const next = [...tbtvTags, clean]
    setTbtvTags(next)
    setTagInput('')
    setShowTagSugg(false)
    persistTbtvTags(next)
  }

  function removeTag(name) {
    const next = tbtvTags.filter(t => t !== name)
    setTbtvTags(next)
    persistTbtvTags(next)
  }

  function handleTagKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (tagInput.trim()) addTag(tagInput)
    } else if (e.key === 'Backspace' && !tagInput && tbtvTags.length) {
      removeTag(tbtvTags[tbtvTags.length - 1])
    }
  }

  const filteredTagSuggestions = allTbtvTags
    .filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !tbtvTags.includes(t))
    .slice(0, 8)

  if (loading) {
    return (
      <div className="sponsors-page">
        <div className="muted">Loading group…</div>
      </div>
    )
  }

  if (!group) {
    return (
      <div className="sponsors-page">
        <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/groups')}>
          <ArrowLeft size={14} /> Back to groups
        </button>
        <div className="sponsors-status error" style={{ marginTop: 12 }}>
          Group {id} not found.
        </div>
      </div>
    )
  }

  const fields = [
    ['id', group.id ?? group.GroupId ?? id],
    ['name', group.name ?? group.Name],
    ['description', group.description ?? group.Description ?? group.descr ?? group.Descr],
    ['status', group.status ?? group.Status],
  ]

  // AS.Group.getInfo response includes linked ads (similar shape to zone).
  const linkedAds = (() => {
    const raw = group?.Ads?.Ad
    if (!raw) return []
    return Array.isArray(raw) ? raw : [raw]
  })()

  const groupName = group.name ?? group.Name ?? `Group ${id}`
  const adspeedDashboardUrl = `https://advertiser.tenband.tv/Groups/show.html?id=${id}`

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/groups')}>
            <ArrowLeft size={14} /> Groups
          </button>
          <h1>{groupName}</h1>
        </div>
        <div className="sponsors-header-right">
          <a
            className="sponsors-refresh-btn"
            href={adspeedDashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={14} /> View in AdSpeed
          </a>
          <button className="sponsors-refresh-btn" onClick={loadGroup}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setEditing(true)}>
            <Edit2 size={14} /> Edit
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.toLowerCase().includes('error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="zone-detail-grid">
        <div>
          <div className="zone-detail-card">
            <h3>Group properties</h3>
            {fields.map(([k, v]) => (
              <div className="zone-detail-field" key={k}>
                <span className="k">{k}</span>
                <span className="v">{v == null || v === '' ? '—' : String(v)}</span>
              </div>
            ))}
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Linked ads ({linkedAds.length})</h3>
              <button
                className="sponsors-refresh-btn"
                onClick={() => setShowLinkAds(true)}
                style={{ fontSize: '0.85rem' }}
              >
                + Link ads
              </button>
            </div>
            {linkedAds.length === 0 ? (
              <div className="muted" style={{ fontSize: '0.85rem' }}>
                No ads linked to this group. Use "Link ads" to add some.
              </div>
            ) : (
              <table className="zones-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedAds.map(a => {
                    const aid = a.id ?? a.AdId
                    return (
                      <tr
                        key={aid}
                        onClick={() => navigate(`/sponsors/ads/${aid}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{aid}</td>
                        <td>{a.name ?? a.Name}</td>
                        <td>{a.status ?? a.Status}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <div className="zone-detail-card">
            <h3>TBTV tags</h3>
            <div style={{ marginBottom: 10 }}>
              {tbtvTags.length === 0 && (
                <span className="muted" style={{ fontSize: '0.85rem' }}>No tags applied</span>
              )}
              {tbtvTags.map(t => (
                <span className="tag-chip" key={t}>
                  {t}
                  <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div className="tag-input-wrap">
              <input
                ref={tagInputRef}
                className="tag-input"
                placeholder="Add tag…"
                value={tagInput}
                onChange={e => { setTagInput(e.target.value); setShowTagSugg(true) }}
                onKeyDown={handleTagKey}
                onFocus={() => setShowTagSugg(true)}
                onBlur={() => setTimeout(() => setShowTagSugg(false), 150)}
              />
              {showTagSugg && filteredTagSuggestions.length > 0 && (
                <div className="tag-suggestions">
                  {filteredTagSuggestions.map(s => (
                    <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
              Shared tag system — same tag namespace as content, zones, ads.
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <GroupEditor
          initial={group}
          onClose={() => setEditing(false)}
          onSubmit={handleEditSubmit}
        />
      )}

      {showLinkAds && (
        <LinkAdsToEntityModal
          kind="group"
          entityId={id}
          entityName={groupName}
          alreadyLinkedAdIds={new Set(linkedAds.map(a => String(a.id ?? a.AdId)))}
          onClose={() => setShowLinkAds(false)}
          onComplete={(ok, fail, lastErr) => {
            setShowLinkAds(false)
            setStatus(fail === 0
              ? `Linked ${ok} ad(s) to this group.`
              : `Linked ${ok}, ${fail} failed. ${lastErr}`)
            loadGroup()
          }}
        />
      )}
    </div>
  )
}
